import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { cn } from 'lib/utils';
import FieldRow from './FieldRow';
import { isValid } from '../../../../../../../utils/validationSummary';

/**
 * Modern (Tailwind) counterpart of components/PropertyDropdown.
 *
 * Same name, same props, same outward handleChange(propertyName, value) contract, so
 * migrating a screen is an import-path change and nothing else.
 *
 * Kept as a class on purpose. componentDidUpdate compares incoming props against the
 * PREVIOUS STATE, not previous props — that is what resyncs the display when the
 * parent clamps or rejects a value (props never change, so a useEffect dep array
 * would not fire). Reproducing that faithfully with hooks needs a ref tracking
 * previous state, i.e. more code than the class, for a component whose state decides
 * what gets written to a driver. Convert later as its own change, if at all.
 *
 * The control is a styled native <select> rather than Radix's Select: same reasoning
 * as StandaloneCheckbox/StandaloneRadio — for a plain value picker the native control
 * is accessible for free, adds no bundle weight, and gets OS-native keyboard
 * type-ahead and scrolling, which matters because some of these lists (fade times)
 * are long. Swapping in shadcn's Select later is a change to this one file.
 */
class PropertyDropdown extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            value: this.props.property.value,
        };

        this.handleChange = this.handleChange.bind(this);
    }

    // The legacy component read e.target.name off a Dropdown.Item; a <select> carries
    // the same datum on e.target.value. The outward contract is unchanged.
    handleChange(e) {
        const value = e.target.value;
        this.setState({ value: value });
        this.props.handleChange(this.props.property.info.name, value);
    }

    transformToValue(value) {
        for (let i = 0; i < this.props.properties.length; i++) {
            if (typeof this.props.properties[i] === 'object' && this.props.properties[i].key === +value) {
                return this.props.properties[i].value;
            }
        }
        return value;
    }

    getDisplayValue(value) {
        const { t } = this.props;
        var v = value
        if (this.props.translate && value !== undefined && value !== null && typeof (v) === 'string')
            v = t(value.replaceAll(' ', '_'));

        const valueTemp = this.transformToValue(v);
        return this.props.propertyDisplayFunc && valueTemp !== undefined && valueTemp !== null
            ? this.props.propertyDisplayFunc(valueTemp)
            : valueTemp;
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.property.value !== prevState.value) {
            this.setState({ value: this.props.property.value });
        }
    }

    render() {
        const options = this.props.properties.map((prop) => {
            const key = typeof prop === 'object' ? prop.key : prop;
            return { key: '' + key, label: this.getDisplayValue(key) };
        });

        const current = '' + (this.state.value ?? '');
        const known = options.some((o) => o.key === current);
        const disabled = this.props.disabled || this.props.properties.length <= 1;

        return (
            <FieldRow label={this.props.title} unit={this.props.measureUnit} disabled={this.props.disabled} className={this.props.className}>
                <div className="relative">
                    <select
                        className={cn(
                            'h-8 w-full appearance-none rounded-md border border-input bg-background pl-2 pr-7 text-sm font-medium',
                            'text-[var(--brand-color1)] shadow-sm outline-none',
                            'focus-visible:ring-1 focus-visible:ring-ring',
                            'disabled:cursor-not-allowed disabled:text-muted-foreground',
                            isValid(this.props.validationSummary, this.props.property) ? '' : 'danger'
                        )}
                        disabled={disabled}
                        name={this.props.property.info.name}
                        value={current}
                        onChange={this.handleChange}>
                        {/* A stored value outside availableValues would otherwise silently
                            display as the first option; surface it instead. */}
                        {!known && <option value={current}>{this.getDisplayValue(this.state.value)}</option>}
                        {options.map((o) => (
                            <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                    </select>
                    <ChevronDown
                        aria-hidden="true"
                        className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                    />
                </div>
            </FieldRow>
        );
    };
}

const mapStateToProps = (state, ownProps) => {
    return {
        validationSummary: state.project.validationSummary,
    };
};

const mapDispatchToProps = {
}

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps),
    withTranslation()
)(PropertyDropdown);
